import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import {
  createTag,
  deleteTag,
  templatesKeys,
  updateTag,
  tagQueryOptions,
} from "@/api/Templates";
import { z } from "zod";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Trans, useLingui } from "@lingui/react/macro";
import { deducedError } from "@/Utils/ErrorUtils";
import { Heading } from "@medoma/components/Heading/Heading";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import BinIcon from "@medoma/components/icons/BinIcon";
import Form from "@/components/Form/Form";
import InputField from "@medoma/components/InputField/InputField";
import { useForm } from "react-hook-form";
import {
  parseGroupDisplayName,
  parseGroupPatientType,
  parseGroupSorting,
} from "../tagUtils";
import styles from "./GroupForm.module.scss";
import { patientTypeDictionary } from "@medoma/models/patients";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import { useParentRoutePatientTypes } from "../templateManagerUtils";

type GroupFormFields = {
  patientType: string;
  name: string;
  sorting?: number;
};

type GroupFormProps =
  | {
      mode: "create";
      id?: never;
      currentName?: never;
      currentPatientType?: never;
      currentSorting?: never;
    }
  | {
      mode: "edit";
      id: string;
      currentName: string;
      currentPatientType: string;
      currentSorting?: number;
    };

const GroupForm = (props: GroupFormProps) => {
  const { mode, id, currentName, currentPatientType, currentSorting } = props;
  const { t } = useLingui();

  const patientTypes = useParentRoutePatientTypes();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<GroupFormFields>({
    defaultValues: {
      patientType:
        currentPatientType ??
        (patientTypes.length === 1 ? patientTypes[0] : undefined),
      name: currentName ?? "",
      sorting: currentSorting,
    },
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: createTagMutation, isPending: isPendingCreate } = useMutation(
    {
      mutationFn: ({
        patientType,
        name,
        sorting,
      }: {
        patientType: string;
        name: string;
        sorting?: number;
      }) => {
        const groupTagString =
          sorting && sorting > 0
            ? `group:${patientType}/${sorting}-${name}`
            : `group:${patientType}/${name}`;
        return createTag(groupTagString);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: templatesKeys.all });
        void navigate("..");
      },
    },
  );

  const { mutate: updateTagMutation, isPending: isPendingUpdate } = useMutation(
    {
      mutationFn: ({
        id,
        newDisplayName,
      }: {
        id: string;
        newDisplayName: string;
      }) => updateTag({ id, newDisplayName }),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: templatesKeys.all });
      },
    },
  );

  const { mutate: deleteTagMutation, isPending: isPendingDelete } = useMutation(
    {
      mutationFn: (id: string) => deleteTag(id),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: templatesKeys.all });
        void navigate("..");
      },
    },
  );

  const validateAndSubmit = handleSubmit((formData) => {
    const groupTagString =
      formData.sorting && formData.sorting > 0
        ? `group:${formData.patientType}/${formData.sorting}-${formData.name}`
        : `group:${formData.patientType}/${formData.name}`;

    if (mode === "create") {
      createTagMutation({
        patientType: formData.patientType,
        name: formData.name,
        sorting: formData.sorting,
      });
    } else {
      updateTagMutation({ id, newDisplayName: groupTagString });
    }
  });

  const isPendingMutation =
    isPendingCreate || isPendingUpdate || isPendingDelete;

  return (
    <Form onSubmit={validateAndSubmit}>
      {patientTypes.length > 1 ? (
        <RadioButtons
          legend={t`Typ av patient`}
          errorMessage={errors.patientType?.message}
          orientation="horizontal"
        >
          {patientTypes.map((type) => (
            <RadioButton
              key={type}
              {...register("patientType", {
                required: {
                  value: true,
                  message: t`Välj vilken typ av mall gruppen ska tillhöra`,
                },
              })}
              label={{ text: t(patientTypeDictionary[type]) }}
              value={type}
              visualStyle="framed"
            />
          ))}
        </RadioButtons>
      ) : null}
      <InputField
        label={t`Namn`}
        showOptionalLabel={false}
        errorMessage={errors.name?.message}
        {...register("name", {
          required: { value: true, message: t`Ange ett namn` },
          pattern: {
            value: /^[^:/-]*$/,
            message: t`Gruppnamnet får inte innehålla :, / eller -`,
          },
        })}
      />
      <InputField
        label={t`Sortering`}
        type="number"
        showOptionalLabel={true}
        description={t`Ange en siffra för att sortera gruppen i listan. Samma ordning visas i Center.`}
        errorMessage={errors.sorting?.message}
        width="full"
        {...register("sorting", {
          valueAsNumber: true,
          min: { value: 1, message: t`Måste vara större än 0` },
        })}
      />
      <div className={styles.actions}>
        <FilledButton type="submit" disabled={isPendingMutation}>
          <Trans>Spara</Trans>
        </FilledButton>
        {mode === "edit" ? (
          <PlainButton
            type="button"
            disabled={isPendingMutation}
            onClick={() => deleteTagMutation(id)}
          >
            <BinIcon />
            <Trans>Ta bort</Trans>
          </PlainButton>
        ) : null}
      </div>
    </Form>
  );
};

export const NewGroupForm = () => {
  return (
    <>
      <Heading level="h1" margin="bottom">
        <Trans>Ny grupp</Trans>
      </Heading>
      <GroupForm key="new" mode="create" />
    </>
  );
};

export const EditGroupForm = () => {
  const { t } = useLingui();
  const { id } = z.object({ id: z.string() }).parse(useParams());

  const {
    data: group,
    isPending,
    isError,
    error,
  } = useQuery(tagQueryOptions(id));

  if (isPending) {
    return <Loading message={t`Hämtar grupp`} />;
  }

  if (isError) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta grupp.`} ${deducedError(error)}`}
      />
    );
  }

  if (!group) {
    return <ErrorMessage message={t`Grupp hittades inte.`} />;
  }

  const groupName = parseGroupDisplayName(group.displayName);
  const groupPatientType = parseGroupPatientType(group.displayName);
  const groupSorting = parseGroupSorting(group.displayName);

  return (
    <>
      <Heading level="h1" margin="bottom">
        {groupName}
      </Heading>
      <GroupForm
        key={id}
        mode="edit"
        id={id}
        currentName={groupName}
        currentPatientType={groupPatientType}
        currentSorting={groupSorting}
      />
    </>
  );
};
